import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ImportedFilesItem,
  LogEntry,
  WorkflowStatus,
  WorkflowsState,
  WorkflowSchemaStateActionModel,
  WorkflowStateMessage,
  WorkflowPayload,
  EnrichedWorkflowComponent
} from '../types'
import { CrossHair, WorkflowLoadMode, WorkflowResultsTabs } from '../enums'
import { name, initialState } from './constants'
import { SQLSchedule } from '../../sql/types'

// TODO: remove isLoading - use selector with boolean && for presence of values
const reducers = {
  setWorkflowSuccess(state: WorkflowsState, action: PayloadAction<WorkflowPayload>) {
    state.workflow = action.payload
  },
  setToolboxLoaded(state: WorkflowsState, action: PayloadAction<boolean>) {
    state.toolboxLoaded = action.payload
  },
  setSelectedId(state: WorkflowsState, action: PayloadAction<string>) {
    state.selectedId = action.payload
  },
  setIsSideBarLeftOpen: (state: WorkflowsState, action: PayloadAction<boolean>) => {
    state.isSideBarLeftOpen = action.payload
  },
  setSelectedTab: (state: WorkflowsState, action: PayloadAction<WorkflowResultsTabs>) => {
    state.selectedTab = action.payload
  },
  setLog: (state: WorkflowsState, action: PayloadAction<LogEntry[]>) => {
    state.log = action.payload
  },
  appendLog: (state: WorkflowsState, action: PayloadAction<LogEntry>) => {
    state.log = state.log.concat([action.payload])
  },
  setCrossHair: (state: WorkflowsState, action: PayloadAction<CrossHair | null>) => {
    state.crossHair = action.payload
  },
  setIsResultsPanelOpen: (state: WorkflowsState, action: PayloadAction<boolean>) => {
    state.isResultsPanelOpen = state.isResultsPanelPermanentOpen || action.payload
    state.isResultsPanelFullScreen = false
  },
  setIsResultsPanelPermanentOpen: (state: WorkflowsState) => {
    state.isResultsPanelPermanentOpen = true
    state.isResultsPanelOpen = true
  },
  showResultTab: (state: WorkflowsState, action: PayloadAction<WorkflowResultsTabs>) => {
    state.isResultsPanelOpen = true
    state.selectedTab = action.payload
  },
  setIsResultsPanelFullScreen: (state: WorkflowsState, action: PayloadAction<boolean>) => {
    state.isResultsPanelFullScreen = action.payload
    state.isResultsPanelOpen = true
  },
  addCreatingDemoWorkflowsId: (state: WorkflowsState, action: PayloadAction<string>) => {
    state.creatingDemoWorkflowsIds.push(action.payload)
  },
  removeCreatingDemoWorkflowsId: (state: WorkflowsState, action: PayloadAction<string>) => {
    state.creatingDemoWorkflowsIds = state.creatingDemoWorkflowsIds.filter((id) => id !== action.payload)
  },
  updateWorkflowStatus: (state: WorkflowsState, action: PayloadAction<WorkflowStatus>) => {
    state.status = action.payload
  },
  setRowsPerPage: (state: WorkflowsState, action: PayloadAction<{ key: string; limit: number }>) => {
    state.meta.rowsPerPage[action.payload.key] = action.payload.limit
  },
  setWorkflowImportedFiles: (state: WorkflowsState, action: PayloadAction<Array<ImportedFilesItem>>) => {
    state.importedFiles = action.payload
  },
  addToImportedFiles: (state: WorkflowsState, action: PayloadAction<ImportedFilesItem>) => {
    if (state.importedFiles?.some((file) => (file.table_name || file.TABLE_NAME) === action.payload.table_name)) return
    state.importedFiles?.push(action.payload)
  },
  removeFromImportedFiles: (state: WorkflowsState, action: PayloadAction<{ fqn: string }>) => {
    state.importedFiles = state.importedFiles?.filter((file) => file.fqn !== action.payload.fqn)
  },
  showMessage: (state: WorkflowsState, action: PayloadAction<WorkflowStateMessage>) => {
    if (state.messages.find((message) => message.key === action.payload.key)) {
      return
    }

    state.messages.push({
      message: action.payload.message,
      severity: action.payload.severity || 'info',
      key: action.payload.key || Date.now().toString()
    })
  },
  hideMessage: (state: WorkflowsState, action: PayloadAction<string>) => {
    state.messages = (state.messages || []).filter((message) => message.key !== action.payload)
  },
  setSchema: (state: WorkflowsState, action: PayloadAction<WorkflowSchemaStateActionModel>) => {
    const schema = state.schemas[action.payload.id]

    state.schemas[action.payload.id] = {
      ...(schema || {}),
      ...action.payload.state
    }
  },
  updateSchemas: (state: WorkflowsState, action: PayloadAction<WorkflowSchemaStateActionModel[]>) => {
    action.payload.forEach((action) => {
      state.schemas[action.id] = {
        ...(state.schemas?.[action.id] || {}),
        ...action.state
      }
    })
  },
  deleteSchema: (state: WorkflowsState, action: PayloadAction<string>) => {
    delete state.schemas[action.payload]
  },
  setAutoSaving: (state: WorkflowsState, action: PayloadAction<boolean>) => {
    state.isAutoSaving = action.payload
  },
  setSchedule(state: WorkflowsState, action: PayloadAction<SQLSchedule | null>) {
    state.schedule = action.payload
  },
  setCanUndo: (state: WorkflowsState, action: PayloadAction<boolean>) => {
    state.canUndo = action.payload
  },
  setCanRedo: (state: WorkflowsState, action: PayloadAction<boolean>) => {
    state.canRedo = action.payload
  },
  setToolboxComponents: (state: WorkflowsState, action: PayloadAction<Record<string, EnrichedWorkflowComponent>>) => {
    state.toolbox.components = action.payload
  },
  setLoadMode: (state: WorkflowsState, action: PayloadAction<WorkflowLoadMode>) => {
    state.loadMode = action.payload
  }
}

export const workflowsSlice = createSlice({
  name,
  initialState,
  // TODO review once we will finish migration
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  reducers
})

export const {
  setIsSideBarLeftOpen,
  setSelectedTab,
  setLog,
  appendLog,
  setCrossHair,
  setIsResultsPanelOpen,
  setIsResultsPanelPermanentOpen,
  showResultTab,
  setIsResultsPanelFullScreen,
  addCreatingDemoWorkflowsId,
  removeCreatingDemoWorkflowsId,
  updateWorkflowStatus,
  setRowsPerPage,
  setWorkflowImportedFiles,
  addToImportedFiles,
  removeFromImportedFiles,
  setAutoSaving,
  // TODO Review top part
  showMessage,
  hideMessage,
  setSchema,
  updateSchemas,
  deleteSchema,
  setSchedule,
  setCanUndo,
  setCanRedo,
  setToolboxComponents,
  setLoadMode
} = workflowsSlice.actions

export default workflowsSlice.reducer
